/// <amd-module name="Repos/Views/CreatePullRequest/Components/CreatePullRequest" />
import * as React from "react";
import { IChangeCollection } from "Repos/ChangesCommon/Contracts";
import { GitVersionDescriptor } from "Repos/Common/Contracts/Git";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import * as Resources from "Repos/Views/CreatePullRequest/Resources";
import { ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { addTabs } from "VSS/Features/PlatformUI/ContributedTab";
import { PerformanceScenario } from "VSS/Platform/Components/Performance";
import { IVssPageContext } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";
import { Components, VssComponent } from "VSS/Platform/Layout";
import { MessageCard } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, TabBadge, TabBar, TabContent, TabProvider } from "VSSUI/Tabs";
import { ICreatePullRequestViewService, IPullRequestCreateInfo } from "../CreatePullRequestViewService";
import { CommitsTabContent } from "./CreatePullRequestCommitsTab";
import { FilesTabContent } from "./CreatePullRequestFilesTab";
import { CreatePullRequestHeader } from "./CreatePullRequestHeader";
import { OverviewTabContent } from "./CreatePullRequestOverviewTab";

class CreatePullRequest extends VssComponent {
    public render() {
        const createPRService = this.context.pageContext.getService<ICreatePullRequestViewService>("ICreatePullRequestViewService");
        return (
            <PerformanceScenario area="Repos" scenarioName="pr-details" tti={true}>
                <Surface background={SurfaceBackground.neutral}>
                    <Page className="repos-pr-create-page flex-grow custom-scrollbar scroll-auto-hide">
                        <CreatePullRequestHeader />
                        <Observer createInfo={createPRService.getCreateInfo()}>
                            {(props: { createInfo: IPullRequestCreateInfo | undefined }) => {
                                return <CreatePullRequestTabs createInfo={props.createInfo} pageContext={this.context.pageContext} />;
                            }}
                        </Observer>
                    </Page>
                </Surface>
            </PerformanceScenario>
        );
    }
}

interface ICreatePullRequestTabsProps {
    createInfo?: IPullRequestCreateInfo;
    pageContext: IVssPageContext;
}

const CreatePullRequestTabs = (props: ICreatePullRequestTabsProps): JSX.Element | null => {
    const { createInfo, pageContext } = props;

    if (!createInfo) {
        return null;
    }

    if (createInfo.activePullRequestId) {
        const routingService = pageContext.getService<IReposRoutingService>("IReposRoutingService");
        const href = routingService.getPullRequestUrl(
            createInfo.sourceTargetInfo.tgtRepository.name,
            createInfo.sourceTargetInfo.tgtRepository.project.name,
            createInfo.activePullRequestId
        );
        return (
            <div className="page-content page-content-top">
                <MessageCard
                    buttonProps={[
                        {
                            href,
                            onClick: e => onClickFPS(pageContext, href, true, e),
                            text: format(Resources.ViewPullRequestFormat, createInfo.activePullRequestId)
                        }
                    ]}
                >
                    {Resources.ActivePullRequestExists}
                </MessageCard>
            </div>
        );
    }

    if (createInfo.commits.length === 0) {
        return (
            <div className="page-content page-content-top">
                <MessageCard>{Resources.NoChangesToMerge}</MessageCard>
            </div>
        );
    }

    const tabs = new ObservableCollection<IVssContributedTab>();
    tabs.push([
        {
            id: "overview",
            name: Resources.Overview,
            render: () => <OverviewTabContent createInfo={createInfo} />
        },
        {
            id: "files",
            name: Resources.Files,
            render: () => <FilesTabContent createInfo={createInfo} />,
            renderBadge: () => {
                return (
                    <Observer changes={createInfo.changes}>
                        {(props: { changes?: IChangeCollection<GitVersionDescriptor> }) => {
                            return props.changes ? (
                                <TabBadge>{props.changes.totalChanges + (props.changes.loadMoreChanges ? "+" : "")}</TabBadge>
                            ) : null;
                        }}
                    </Observer>
                );
            }
        },
        {
            badgeCount: createInfo.commits.length,
            id: "commits",
            name: Resources.Commits,
            render: () => <CommitsTabContent createInfo={createInfo} />,
            renderBadge: () => (
                <TabBadge>{createInfo.commits.length > createInfo.maxNumberOfCommits ? `${createInfo.maxNumberOfCommits}+` : createInfo.commits.length}</TabBadge>
            )
        }
    ] as IVssContributedTab[]);

    addTabs(pageContext, tabs, "ms.vss-code-web.pr-create-tabs");

    const selectedTabId = new ObservableValue("overview");

    return (
        <TabProvider providers={tabs} selectedTabId={selectedTabId}>
            <TabBar onSelectedTabChanged={tabId => (selectedTabId.value = tabId)} />
            <TabContent />
        </TabProvider>
    );
};

Components.add("repos-create-pr-component", CreatePullRequest);
